import React from "react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AdminAdMetrics from "../components/AdminAdMetrics";
import AdminPayouts from "../components/AdminPayouts";

export default function AdminDashboard() {
    const [stats, setStats] = React.useState({});
    const [users, setUsers] = React.useState([]);
    const [videos, setVideos] = React.useState([]);
    const [campaigns, setCampaigns] = React.useState([]);

    React.useEffect(() => {
        Promise.all([
            api.get("/admin/stats"),
            api.get("/admin/users"),
            api.get("/admin/videos"),
            api.get("/admin/campaigns"),
        ]).then(([s, u, v, c]) => {
            setStats(s.data);
            setUsers(u.data);
            setVideos(v.data);
            setCampaigns(c.data);
        });
    }, []);

    return (
        <div className="min-h-screen bg-white text-slate-900" data-testid="admin-dashboard">
            <Navbar />
            <section className="border-b hairline surface-warm">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-10">
                    <div className="chip chip-coral inline-flex mb-3">ADMIN</div>
                    <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
                        Operations
                    </h1>
                </div>
            </section>

            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-6 gap-3">
                {[
                    ["Users", stats.users],
                    ["Creators", stats.creators],
                    ["Brands", stats.brands],
                    ["Videos", stats.videos],
                    ["Campaigns", stats.campaigns],
                    ["Licenses", stats.licenses],
                ].map(([l, v]) => (
                    <div key={l} className="card-ecom p-4">
                        <div className="text-xs text-slate-500">{l}</div>
                        <div className="font-display text-2xl font-semibold mt-1">{v ?? 0}</div>
                    </div>
                ))}
            </section>

            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8">
                <Tabs defaultValue="payouts" className="w-full">
                    <TabsList
                        className="rounded-full bg-slate-100 p-1 h-auto flex-wrap"
                        data-testid="admin-tabs"
                    >
                        {["payouts", "ad-metrics", "users", "videos", "campaigns"].map((t) => (
                            <TabsTrigger
                                key={t}
                                value={t}
                                className="rounded-full data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-5 py-2 capitalize"
                                data-testid={`tab-${t}`}
                            >
                                {t.replace("-", " ")}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="payouts" className="mt-6">
                        <AdminPayouts />
                    </TabsContent>

                    <TabsContent value="ad-metrics" className="mt-6">
                        <AdminAdMetrics />
                    </TabsContent>

                    <TabsContent value="users" className="mt-6">
                        <div className="card-ecom divide-y hairline" data-testid="users-list">
                            {users.map((u) => (
                                <div
                                    key={u.id}
                                    className="p-4 flex items-center justify-between"
                                    data-testid={`user-${u.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-coral to-lime flex items-center justify-center text-white font-display font-bold">
                                            {u.name?.[0]?.toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="font-display font-semibold">
                                                {u.name}
                                            </div>
                                            <div className="text-xs text-slate-500">{u.email}</div>
                                        </div>
                                    </div>
                                    <span className="chip chip-coral">{u.role}</span>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="videos" className="mt-6">
                        <div className="card-ecom divide-y hairline" data-testid="videos-list">
                            {videos.map((v) => (
                                <div
                                    key={v.id}
                                    className="p-4 flex items-center justify-between flex-wrap gap-2"
                                    data-testid={`vid-${v.id}`}
                                >
                                    <div>
                                        <div className="font-display font-semibold">{v.title}</div>
                                        <div className="text-xs text-slate-500">
                                            {v.creator_name} · {v.category} · {v.views || 0} views
                                        </div>
                                    </div>
                                    <span
                                        className={`chip ${v.is_deleted ? "chip-coral" : "chip-success"}`}
                                    >
                                        {v.is_deleted ? "deleted" : v.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="campaigns" className="mt-6">
                        <div className="card-ecom divide-y hairline" data-testid="campaigns-list">
                            {campaigns.map((c) => (
                                <div
                                    key={c.id}
                                    className="p-4 flex items-center justify-between"
                                    data-testid={`camp-${c.id}`}
                                >
                                    <div>
                                        <div className="font-display font-semibold">{c.name}</div>
                                        <div className="text-xs text-slate-500">
                                            {c.brand_name} · {c.objective}
                                        </div>
                                    </div>
                                    <span className="chip chip-neutral">{c.status}</span>
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </section>
        </div>
    );
}
